import { useEffect, useRef } from "react";

import { getPlainTextFromHtml } from "../../components/RichTextEditor";
import {
  addTicketMessage,
  createTicket,
  deleteTicket,
  downloadTicketAttachment,
  getTicket,
  isRequestCanceled,
  markNotificationAsRead,
  updateTicketAction,
  updateTicketAssignment,
} from "./api";
import {
  MAX_ATTACHMENT_SIZE,
  MAX_TICKET_ATTACHMENTS,
  initialTicket,
} from "./constants";
import { getApiErrorMessage } from "./formatters";

export function useTicketActions({
  adminComment,
  adminStatus,
  authHeaders,
  developmentTimeMinutes,
  fetchDashboard,
  messageDraft,
  messageFiles,
  selectedTicket,
  setActiveView,
  setAdminComment,
  setAdminStatus,
  setDevelopmentTimeMinutes,
  setIsDeleteTicketDialogOpen,
  setLoading,
  setMessageDraft,
  setMessageFiles,
  setSelectedTicket,
  setTicketFiles,
  setTicketForm,
  ticketFiles,
  ticketForm,
  toast,
  token,
  selectedTicketIdRef,
  updateSelectedTicketId,
}) {
  const openTicketRequestIdRef = useRef(0);
  const openTicketControllerRef = useRef(null);

  useEffect(() => () => {
    openTicketRequestIdRef.current += 1;
    openTicketControllerRef.current?.abort();
  }, []);

  const openTicket = async (ticketId) => {
    const requestId = openTicketRequestIdRef.current + 1;
    openTicketRequestIdRef.current = requestId;
    openTicketControllerRef.current?.abort();
    const controller = new AbortController();
    openTicketControllerRef.current = controller;

    updateSelectedTicketId(ticketId);
    try {
      const response = await getTicket(ticketId, {
        ...authHeaders,
        signal: controller.signal,
      });
      if (
        controller.signal.aborted ||
        openTicketRequestIdRef.current !== requestId ||
        selectedTicketIdRef.current !== ticketId
      ) {
        return;
      }
      setSelectedTicket(response.data);
      setAdminStatus(response.data.status);
      setDevelopmentTimeMinutes(response.data.development_time_minutes ?? "");
      setActiveView("ticket-detail");
    } catch (error) {
      if (isRequestCanceled(error)) return;
      toast({
        title: "Ticket indisponible",
        description: "Impossible de charger ce ticket.",
        variant: "destructive",
      });
    } finally {
      if (openTicketRequestIdRef.current === requestId) {
        openTicketControllerRef.current = null;
      }
    }
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();
    if (!getPlainTextFromHtml(ticketForm.description)) {
      toast({
        title: "Description requise",
        description: "Ajoute le contenu de ta demande avant l'envoi.",
        variant: "destructive",
      });
      return;
    }
    if (Array.from(ticketFiles).length > MAX_TICKET_ATTACHMENTS) {
      toast({
        title: "Trop de fichiers",
        description: `Vous pouvez ajouter jusqu'à ${MAX_TICKET_ATTACHMENTS} pièces jointes par ticket.`,
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.append("title", ticketForm.title);
    formData.append("category", ticketForm.category);
    formData.append("site_url", ticketForm.site_url);
    formData.append("description", ticketForm.description);
    Array.from(ticketFiles).forEach((file) => formData.append("attachments", file));

    setLoading(true);
    try {
      await createTicket(formData, token);
      setTicketForm(initialTicket);
      setTicketFiles([]);
      setActiveView("tickets");
      await fetchDashboard();
      toast({
        title: "Ticket envoye",
        description: "Votre demande de modification a bien ete transmise.",
      });
    } catch (error) {
      toast({
        title: "Création impossible",
        description: getApiErrorMessage(error, "Le ticket n'a pas pu être créé."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTicketFilesChange = (event) => {
    const files = Array.from(event.target.files || []);

    if (files.length > MAX_TICKET_ATTACHMENTS) {
      event.target.value = "";
      setTicketFiles([]);
      toast({
        title: "Trop de fichiers",
        description: `Vous pouvez ajouter jusqu'à ${MAX_TICKET_ATTACHMENTS} pièces jointes par ticket.`,
        variant: "destructive",
      });
      return;
    }

    setTicketFiles(event.target.files);
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!selectedTicket) return;
    if (!getPlainTextFromHtml(messageDraft) && messageFiles.length === 0) {
      toast({
        title: "Reponse vide",
        description: "Ajoutez un message ou au moins une pièce jointe.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("content", messageDraft);
      messageFiles.forEach((file) => formData.append("attachments", file));
      const response = await addTicketMessage(selectedTicket.id, formData, authHeaders);
      setSelectedTicket(response.data);
      setMessageDraft("");
      setMessageFiles([]);
      await fetchDashboard();
    } catch (error) {
      toast({
        title: "Message non envoye",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMessageFilesChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > MAX_TICKET_ATTACHMENTS) {
      event.target.value = "";
      setMessageFiles([]);
      toast({
        title: "Trop de fichiers",
        description: `Vous pouvez ajouter jusqu'à ${MAX_TICKET_ATTACHMENTS} pièces jointes par reponse.`,
        variant: "destructive",
      });
      return;
    }
    const oversizedFile = files.find((file) => file.size > MAX_ATTACHMENT_SIZE);
    if (oversizedFile) {
      event.target.value = "";
      setMessageFiles([]);
      toast({
        title: "Fichier trop volumineux",
        description: `${oversizedFile.name} depasse la limite de 5 Mo.`,
        variant: "destructive",
      });
      return;
    }
    setMessageFiles(files);
  };

  const handleStatusUpdate = async (event) => {
    event.preventDefault();
    if (!selectedTicket) return;

    setLoading(true);
    try {
      const normalizedMessage = getPlainTextFromHtml(adminComment) ? adminComment : null;
      const normalizedDevelopmentTime =
        developmentTimeMinutes === "" || developmentTimeMinutes === null ? null : Number(developmentTimeMinutes);

      if (
        !normalizedMessage &&
        messageFiles.length === 0 &&
        adminStatus === selectedTicket.status &&
        normalizedDevelopmentTime === null
      ) {
        toast({
          title: "Aucune action",
          description: "Ajoute un message, une pièce jointe, change le statut ou saisis un temps passe.",
          variant: "destructive",
        });
        return;
      }

      const formData = new FormData();
      if (normalizedMessage) formData.append("content", normalizedMessage);
      if (adminStatus !== selectedTicket.status) {
        formData.append("status", adminStatus);
      }
      if (normalizedDevelopmentTime !== null) {
        formData.append("development_time_minutes", normalizedDevelopmentTime);
      }
      messageFiles.forEach((file) => formData.append("attachments", file));

      const response = await updateTicketAction(
        selectedTicket.id,
        formData,
        authHeaders
      );
      setSelectedTicket(response.data);
      setAdminComment("");
      setMessageFiles([]);
      setDevelopmentTimeMinutes(response.data.development_time_minutes ?? "");
      await fetchDashboard();
      toast({
        title: "Ticket mis à jour",
        description: "L'action a ete enregistree.",
      });
    } catch (error) {
      toast({
        title: "Mise à jour impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTicketAssignmentChange = async (developerId) => {
    if (!selectedTicket) return;

    setLoading(true);
    try {
      const response = await updateTicketAssignment(
        selectedTicket.id,
        { developer_id: developerId || null },
        authHeaders
      );
      setSelectedTicket(response.data);
      await fetchDashboard();
      toast({
        title: developerId ? "Ticket attribué" : "Affectation retirée",
        description: developerId
          ? "Le développeur a été notifié par email."
          : "Le ticket n'est plus attribué à un développeur.",
      });
    } catch (error) {
      toast({
        title: "Affectation impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!selectedTicket) return;

    setLoading(true);
    try {
      await deleteTicket(selectedTicket.id, authHeaders);
      setIsDeleteTicketDialogOpen(false);
      setSelectedTicket(null);
      updateSelectedTicketId("");
      setAdminComment("");
      setDevelopmentTimeMinutes("");
      setActiveView("tickets");
      await fetchDashboard();
      toast({
        title: "Ticket supprime",
        description: "Le ticket et ses notifications ont ete supprimes.",
      });
    } catch (error) {
      toast({
        title: "Suppression impossible",
        description: getApiErrorMessage(error, "Seuls les tickets en cours de traitement peuvent être supprimés."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId, authHeaders);
      await fetchDashboard();
    } catch (error) {
      toast({
        title: "Notification",
        description: "Impossible de marquer cette notification comme lue.",
        variant: "destructive",
      });
    }
  };

  const downloadAttachment = async (attachment) => {
    if (!selectedTicket) return;

    try {
      const response = await downloadTicketAttachment(selectedTicket.id, attachment.id, authHeaders);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Telechargement impossible",
        description: "La pièce jointe n'a pas pu être récupérée.",
        variant: "destructive",
      });
    }
  };

  return {
    downloadAttachment,
    handleCreateTicket,
    handleDeleteTicket,
    handleMessageFilesChange,
    handleSendMessage,
    handleStatusUpdate,
    handleTicketAssignmentChange,
    handleTicketFilesChange,
    markAsRead,
    openTicket,
  };
}
